import type { FrameStatus } from 'components/annotation-page/workspace/steps/steps-api';
import { modelSpecOf } from 'components/annotation-page/workspace/references/reference-model';
import { plural, t } from 'utils/i18n';

export interface RawShape {
    frame: number;
    label_id: number;
    attributes: { spec_id: number; value: string }[];
}

export interface LabelInfo {
    id: number;
    name: string;
    color?: string;
    attributes: { id: number; name: string }[];
}

export interface Category {
    name: string;
    color: string;
    total: number;
    withModel: number;
}

export interface Coverage {
    total: number;
    withModel: number;
    // most detections first
    categories: Category[];
    frames: Record<string, FrameStatus>;
}

// which detections already point at a library model, by category and by photo
export function coverage(shapes: RawShape[], labels: LabelInfo[]): Coverage {
    const specs = new Map<number, { label: LabelInfo; specId: number }>();
    labels.forEach((label) => {
        const spec = modelSpecOf(label);
        if (spec) specs.set(label.id, { label, specId: spec.id });
    });
    const byLabel = new Map<number, Category>();
    const byFrame = new Map<number, { total: number; withModel: number }>();
    let total = 0;
    let withModel = 0;
    shapes.forEach((shape) => {
        const spec = specs.get(shape.label_id);
        if (!spec) return;
        const value = shape.attributes.find((attr) => attr.spec_id === spec.specId)?.value?.trim() ?? '';
        const has = value ? 1 : 0;
        total += 1;
        withModel += has;
        const category = byLabel.get(shape.label_id) ?? {
            name: spec.label.name, color: spec.label.color ?? '#ddd', total: 0, withModel: 0,
        };
        category.total += 1;
        category.withModel += has;
        byLabel.set(shape.label_id, category);
        const frame = byFrame.get(shape.frame) ?? { total: 0, withModel: 0 };
        frame.total += 1;
        frame.withModel += has;
        byFrame.set(shape.frame, frame);
    });
    const frames: Record<string, FrameStatus> = {};
    byFrame.forEach((count, frame) => {
        if (count.withModel === count.total) frames[String(frame)] = 'done';
        else if (count.withModel > 0) frames[String(frame)] = 'partial';
        else frames[String(frame)] = 'pending';
    });
    return {
        total,
        withModel,
        categories: [...byLabel.values()].sort((a, b) => b.total - a.total || a.name.localeCompare(b.name)),
        frames,
    };
}

export function coverageLabel(c: Coverage | null): string {
    if (!c) return '';
    if (c.total === 0) return t('no detections yet');
    return t('{count}, {n} with a library model', { count: plural(c.total, 'detection'), n: c.withModel });
}
